import { type ReactNode, useState } from "react";
import { useParams } from "react-router";

import { useWifiLink } from "@/lib/api";

import { ConfirmStep } from "./confirm-step";
import { CredentialsForm } from "./credentials-form";
import { FaqSection } from "./faq-section";
import { KitExplainer } from "./kit-explainer";
import { LinkErrorState } from "./link-error-state";
import { PageFooter, PageHeader } from "./page-chrome";
import { SetupModal } from "./setup-modal";
import { SuccessState } from "./success-state";

function LoadingState() {
  return (
    <main
      className="mx-auto grid max-w-[1320px] gap-10 px-5 pt-10 pb-16 sm:px-8 sm:pt-14 lg:grid-cols-[0.88fr_1.12fr] lg:gap-12 lg:px-10 lg:pt-16"
      aria-busy="true"
      aria-label="Loading your sharing link"
    >
      <div className="space-y-4">
        <div className="bg-border-subtle h-4 w-40 animate-pulse rounded" />
        <div className="bg-border-subtle h-16 w-full animate-pulse rounded" />
        <div className="bg-border-subtle h-16 w-3/4 animate-pulse rounded" />
        <div className="bg-border-subtle h-[280px] w-full animate-pulse rounded-2xl" />
      </div>
      <div className="bg-border-subtle h-[420px] animate-pulse rounded-[28px]" />
    </main>
  );
}

/**
 * Public Wi-Fi credential sharing page.
 *
 * Owns the step machine and nothing else. There are three live steps (confirm,
 * form, success) and five terminal ones, all driven by the link status the
 * server returns. No session is involved: the token in the URL is the only
 * credential, and the identity answer is carried in component state from the
 * confirm step into the submit call so the server can re-check it.
 */
export function WifiLinkPage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, isError } = useWifiLink(token);

  const [identityAnswer, setIdentityAnswer] = useState<string | null>(null);
  const [submittedNetwork, setSubmittedNetwork] = useState<string | null>(null);
  const [showSetup, setShowSetup] = useState(false);

  const shell = (children: ReactNode) => (
    <div className="bg-background min-h-screen">
      <PageHeader />
      {children}
      <PageFooter />
    </div>
  );

  if (isLoading) {
    return shell(<LoadingState />);
  }

  if (isError || !data) {
    return shell(<LinkErrorState kind="unknown" />);
  }

  if (submittedNetwork) {
    return shell(<SuccessState networkName={submittedNetwork} />);
  }

  if (data.status !== "pending") {
    return shell(<LinkErrorState kind={data.status} />);
  }

  return (
    <div className="bg-background min-h-screen">
      <PageHeader />
      <main>
        <div className="mx-auto grid max-w-[1320px] gap-10 px-5 pt-10 pb-16 sm:px-8 sm:pt-14 lg:grid-cols-[0.88fr_1.12fr] lg:gap-12 lg:px-10 lg:pt-16 lg:pb-24">
          <section className="lg:py-4">
            {identityAnswer === null ? (
              <ConfirmStep
                token={token as string}
                hintEmail={data.hint_email}
                hintName={data.hint_name}
                onConfirmed={setIdentityAnswer}
              />
            ) : (
              <CredentialsForm
                token={token as string}
                identityAnswer={identityAnswer}
                onSubmitted={(networkName) => {
                  setSubmittedNetwork(networkName);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
          </section>

          {/* Stays mounted across confirm and form: the reassurance needs to be
              on screen while the household decides whether to trust the page. */}
          <KitExplainer onShowSetup={() => setShowSetup(true)} />
        </div>

        <FaqSection />
      </main>
      <PageFooter />
      <SetupModal open={showSetup} onClose={() => setShowSetup(false)} />
    </div>
  );
}
